import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Zap, Shield, Network, Clock, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const clinicTypes = ["Med Spas", "Men's Health", "Hormone Clinics", "Weight Loss", "Longevity", "Telehealth", "Labs", "Pharmacy Partners"];

const problemCards = [
  { icon: Shield, title: "Research-Use-Only Risk", desc: "Avoid routing patient care through inappropriate research-use-only sourcing." },
  { icon: Network, title: "Pharmacy Confusion", desc: "Understand which pathway may fit your clinic model, provider status, and state coverage." },
  { icon: Zap, title: "Vendor Overload", desc: "Stop chasing random vendors and start with a trusted clinical access process." },
  { icon: Clock, title: "Operational Bottlenecks", desc: "Connect pharmacy, labs, fulfillment, telehealth, and support into one cleaner workflow." },
];

const solutionSteps = [
  { num: "01", title: "Tell AvA about your clinic", desc: "Clinic type, provider status, states served, current services, and growth goals." },
  { num: "02", title: "Get routed to the right pathway", desc: "Pharmacy fulfillment, lab integration, telehealth support, vendor access, or live Apexum team handoff." },
  { num: "03", title: "Build with trusted relationships", desc: "Apexum supports qualified clinics through established pharmacy and infrastructure relationships." },
];

function MetricCounter({ value, label }) {
  const [displayed, setDisplayed] = useState("0");
  const ref = useRef(null);

  useEffect(() => {
    const match = String(value).match(/^(\d+)(.*)$/);
    if (!match) { setDisplayed(value); return; }
    const num = parseInt(match[1]);
    const suffix = match[2] || "";
    if (isNaN(num)) { setDisplayed(value); return; }
    let start = 0;
    const duration = 1500;
    const step = duration / num;
    const timer = setInterval(() => {
      start = Math.min(start + Math.ceil(num / 30), num);
      setDisplayed(start.toString() + suffix);
      if (start >= num) clearInterval(timer);
    }, step);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <div data-testid={HOME.metricsSection} className="text-center p-6">
      <div className="text-4xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {displayed}
      </div>
      <div className="text-sm text-slate-500 font-medium">{label}</div>
    </div>
  );
}

export default function Home() {
  const [metrics, setMetrics] = useState([]);

  useEffect(() => {
    axios.get(`${API}/metrics/public`).then(r => setMetrics(r.data.metrics || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        {/* Background blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 right-0 w-[600px] h-[600px] bg-cyan-100 rounded-full blur-3xl opacity-40 animate-blob" />
          <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] bg-blue-100 rounded-full blur-3xl opacity-30 animate-blob animation-delay-2000" />
          <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-teal-100 rounded-full blur-3xl opacity-25 animate-blob animation-delay-4000" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 border border-blue-100">
              <Star size={12} fill="currentColor" />
              The Clinical Access Connector
            </div>
            <h1 data-testid={HOME.heroTitle}
              className="text-5xl sm:text-6xl font-medium leading-tight tracking-tight text-slate-900 mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              Meet <span className="gradient-text">AvA</span>, Your<br />Clinical Access Connector
            </h1>
            <p className="text-sm text-blue-500 font-medium mb-5">
              AvA — Apexum's Verified Access
            </p>
            <p data-testid={HOME.heroSubtitle} className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
              AvA helps qualified clinics, med spas, men's health practices, and telehealth companies connect with Apexum's trusted 503A/503B pharmacy pathways, lab partners, fulfillment support, and performance-health infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link to="/talk-to-ava" data-testid={HOME.ctaTalkToAva}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-3.5 rounded-full font-medium hover:bg-blue-700 transition-all duration-200 shadow-lg shadow-blue-600/25">
                Talk to AvA
                <ArrowRight size={18} />
              </Link>
              <Link to="/how-it-works" data-testid={HOME.ctaHowItWorks}
                className="inline-flex items-center justify-center gap-2 bg-white text-slate-800 px-8 py-3.5 rounded-full font-medium border border-slate-200 hover:bg-slate-50 transition-all duration-200">
                See How It Works
              </Link>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <CheckCircle size={16} className="text-emerald-500" />
              No upfront onboarding fee for qualified clinics.
            </div>
          </motion.div>

          {/* Right - AvA Chat Mockup */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }} className="relative flex justify-center lg:justify-end">

            {/* Floating cards */}
            <motion.div animate={{ y: [-6, 6, -6] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-lg px-4 py-3 text-xs font-medium text-slate-700 border border-slate-100 z-10">
              503A / 503B Pathways
            </motion.div>
            <motion.div animate={{ y: [6, -6, 6] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-16 -right-6 bg-white rounded-2xl shadow-lg px-4 py-3 text-xs font-medium text-slate-700 border border-slate-100 z-10">
              Lab Integration
            </motion.div>
            <motion.div animate={{ y: [-4, 8, -4] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-24 -left-8 bg-white rounded-2xl shadow-lg px-4 py-3 text-xs font-medium text-slate-700 border border-slate-100 z-10">
              Telehealth Support
            </motion.div>
            <motion.div animate={{ y: [8, -4, 8] }} transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-8 -right-4 bg-white rounded-2xl shadow-lg px-4 py-3 text-xs font-medium text-slate-700 border border-slate-100 z-10">
              Live Team Handoff
            </motion.div>

            {/* Phone mockup */}
            <div className="w-72 bg-white rounded-3xl shadow-2xl shadow-blue-600/10 border border-slate-100 overflow-hidden">
              {/* Chat header */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/30 flex-shrink-0">
                  <img src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
                    alt="AvA" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">AvA</div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-white/80 text-xs">Clinical Access Connector</span>
                  </div>
                </div>
              </div>

              {/* Chat messages */}
              <div className="p-4 space-y-3 bg-slate-50">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                  className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm text-xs leading-relaxed max-w-[85%]">
                  Hi, I'm AvA with Apexum Partners. I help qualified clinics find the right pharmacy and clinical infrastructure pathway.
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
                  className="bg-white px-4 py-3 rounded-2xl rounded-tr-sm text-xs text-slate-700 max-w-[75%] ml-auto shadow-sm border border-slate-100">
                  We're a hormone clinic in Texas
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}
                  className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm text-xs leading-relaxed max-w-[85%]">
                  Great! Let me help route you to the right Apexum pathway. Can I get your name and role at the clinic?
                </motion.div>
              </div>

              {/* Input bar */}
              <div className="px-4 py-3 border-t border-slate-100 bg-white flex items-center gap-2">
                <div className="flex-1 bg-slate-50 rounded-full px-4 py-2 text-xs text-slate-400">
                  Type your message...
                </div>
                <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
                  <ArrowRight size={12} className="text-white" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Strip */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-blue-600 mb-6">
            Built for modern clinical operators
          </p>
          <div data-testid={HOME.categoryStrip}
            className="flex flex-wrap justify-center gap-3">
            {clinicTypes.map(t => (
              <span key={t} className="bg-white px-5 py-2 rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <h2 className="text-4xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The demand is here.<br />The sourcing path has to be smarter.
            </h2>
            <p className="text-lg text-slate-600">
              Patients are asking clinics about peptides, GLP-1 support, NAD+, hormone optimization, longevity, and performance-health services. But many providers are stuck sorting through unclear sourcing options.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                  className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-300">
                  <div className="w-11 h-11 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{card.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-4xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AvA qualifies, routes, and connects.
            </h2>
            <p className="text-lg text-slate-600">
              AvA is Apexum's AI-powered front door. She collects the right clinic details, identifies needs, and routes to the appropriate pathway.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {solutionSteps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }} viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-center text-3xl font-medium text-slate-900 mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Clinical access, simplified.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {metrics.length > 0 ? metrics.map((m, i) => (
              <MetricCounter key={i} value={m.value} label={m.label} />
            )) : [
              { value: "0", label: "Upfront Onboarding Fee" },
              { value: "503A/503B", label: "Pathway Support" },
              { value: "Live", label: "Team Handoff" },
              { value: "Minutes", label: "Clinic Qualification" },
            ].map((m, i) => (
              <div key={i} className="text-center p-6">
                <div className="text-3xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{m.value}</div>
                <div className="text-sm text-slate-500">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet AvA Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }} viewport={{ once: true }}
            className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_40px_rgb(0,0,0,0.06)] overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Photo side */}
              <div className="relative bg-gradient-to-br from-blue-600 to-cyan-500 flex items-end justify-center pt-10 pb-0 px-10 min-h-[360px]">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-700/80 to-cyan-600/60" />
                {/* Decorative blobs */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-blue-300/20 rounded-full blur-2xl" />
                <div className="relative z-10 w-64">
                  <img
                    src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
                    alt="AvA Sterling — Clinical Access Connector"
                    className="w-full object-cover object-top rounded-t-3xl shadow-2xl"
                    style={{ maxHeight: 340 }}
                  />
                </div>
              </div>

              {/* Content side */}
              <div className="p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-5 border border-blue-100 self-start">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  Available Now
                </div>
                <h2 className="text-4xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Meet <span className="gradient-text">AvA</span>
                </h2>
                <p className="text-base text-slate-500 font-medium mb-4">Clinical Access Connector · Apexum Partners</p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  AvA guides qualified clinic operators, licensed providers, and healthcare entrepreneurs through a thoughtful intake — understanding your clinic model, provider status, and pathway goals before routing you to the right Apexum access pathway.
                </p>
                <div className="space-y-2 mb-8">
                  {[
                    "AI-powered, warm, and compliance-aware",
                    "Available online or by phone",
                    "Designed for licensed clinical operators",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                      <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
                <Link to="/talk-to-ava"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-full font-medium hover:bg-blue-700 transition-all duration-200 shadow-lg shadow-blue-600/25 self-start">
                  Talk to AvA
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Qualify Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            See if your clinic qualifies
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            AvA is designed for licensed providers, clinic owners, medical directors, med spas, men's health clinics, hormone clinics, longevity clinics, weight-loss clinics, and telehealth groups.
          </p>
          <Link to="/talk-to-ava"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-10 py-4 rounded-full font-medium text-lg hover:bg-blue-700 transition-all duration-200 shadow-lg shadow-blue-600/25">
            Start Qualification
            <ArrowRight size={20} />
          </Link>
          <p className="mt-6 text-sm text-slate-400 max-w-md mx-auto">
            Apexum does not provide medical advice, prescribe medication, or route consumers to research-use-only products for human use.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-700/50 to-cyan-700/50" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to find the right pathway?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Talk to AvA and see if your clinic qualifies.
          </p>
          <Link to="/talk-to-ava"
            className="inline-flex items-center gap-2 bg-white text-blue-700 px-10 py-4 rounded-full font-semibold text-lg hover:bg-blue-50 transition-all duration-200 shadow-xl">
            Start with AvA
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
